import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useStickyHorizontalScroll } from '../hooks/useStickyHorizontalScroll';
import { useTableColumnResize } from '../hooks/useTableColumnResize';
import useStickyTableHeader from '../../VariablesPanel/VariablesTable/hooks/useStickyTableHeader';
import {
  emptyCellBlocks,
  normalizeTableData,
  tableContentEqual,
  tableNeedsCellMigration,
} from '../utils/tableCellBlocks';
import { TableCellCanvas } from './TableCellCanvas';

/**
 * TableBlock — a grid of cells. Each cell is a mini block list, so "/"
 * inserts the same blocks as on the page (file upload, checkbox, image…).
 * Cell blocks participate in page DnD (handles + drag in/out). Tab still
 * walks cells; Enter is the block editor's Enter inside the cell.
 */
export const TableBlock = ({
  block,
  onUpdate,
  ...editorProps
}) => {
  const rawTableData = block.props?.tableData;
  const [tableData, setTableData] = useState(() => normalizeTableData(rawTableData));
  const tableDataRef = useRef(tableData);
  tableDataRef.current = tableData;

  const tableElRef = useRef(null);
  const { sentinelRef, headerRef, stuck } = useStickyTableHeader({
    scopeSelector: '.table-block-wrapper',
  });
  const { scrollRef: wrapperRef, bar: stickyHScrollBar } = useStickyHorizontalScroll(
    true,
    `${tableData.headers.length}x${tableData.rows.length}:${(tableData.columnWidths || []).join(',')}`
  );

  const commitTableData = useCallback(
    (next) => {
      tableDataRef.current = next;
      setTableData(next);
      onUpdate(block.id, { props: { ...block.props, tableData: next } });
    },
    [block.id, block.props, onUpdate]
  );

  const applyWidths = useCallback((columnWidths) => {
    const next = { ...tableDataRef.current, columnWidths };
    tableDataRef.current = next;
    setTableData(next);
  }, []);

  const commitWidths = useCallback(
    (columnWidths) => {
      commitTableData({ ...tableDataRef.current, columnWidths });
    },
    [commitTableData]
  );

  const { startResize, isResizing } = useTableColumnResize({
    tableRef: tableElRef,
    colCount: tableData.headers.length,
    onLive: applyWidths,
    onCommit: commitWidths,
  });

  // Undo / paste / load: take incoming when content actually changed.
  // String cells compare equal to their migrated text blocks so a slow
  // persist does not mint new ids and kill the caret.
  useEffect(() => {
    if (isResizing) return;
    if (tableContentEqual(rawTableData || {}, tableDataRef.current)) return;
    const incoming = normalizeTableData(rawTableData);
    tableDataRef.current = incoming;
    setTableData(incoming);
  }, [rawTableData, block.id, isResizing]);

  // Persist one-time string → block-list migration
  useLayoutEffect(() => {
    if (!tableNeedsCellMigration(rawTableData)) return;
    onUpdate(block.id, {
      props: { ...block.props, tableData: tableDataRef.current },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;
    const handleWheel = (e) => {
      // Vertical wheel scrolls the page. A wide table used to steal it and
      // pan sideways, so the document stuttered whenever the cursor crossed one.
      const horizontal = e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (!horizontal) return;
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0 || wrapper.scrollWidth <= wrapper.clientWidth) return;
      e.preventDefault();
      wrapper.scrollLeft += delta;
    };
    wrapper.addEventListener('wheel', handleWheel, { passive: false });
    return () => wrapper.removeEventListener('wheel', handleWheel);
  }, [wrapperRef]);

  const handleCellBlocksChange = useCallback(
    (rowIndex, colIndex, nextBlocks) => {
      const current = tableDataRef.current;
      const next = {
        ...current,
        headers: [...current.headers],
        rows: current.rows.map((row) => [...row]),
      };
      if (rowIndex === -1) {
        next.headers = next.headers.slice();
        next.headers[colIndex] = nextBlocks;
      } else if (next.rows[rowIndex]) {
        next.rows[rowIndex][colIndex] = nextBlocks;
      }
      commitTableData(next);
    },
    [commitTableData]
  );

  const focusCell = useCallback(
    (rowIndex, colIndex) => {
      setTimeout(() => {
        const cell = document.querySelector(
          `[data-table-cell="${block.id}-${rowIndex}-${colIndex}"] .doc-editable`
        );
        cell?.focus();
      }, 0);
    },
    [block.id]
  );

  const addColumn = () => {
    const current = tableDataRef.current;
    commitTableData({
      ...current,
      headers: [...current.headers, emptyCellBlocks()],
      rows: current.rows.map((row) => [...row, emptyCellBlocks()]),
      ...(current.columnWidths
        ? {
            columnWidths: [
              ...current.columnWidths,
              current.columnWidths.reduce((a, b) => a + b, 0) /
                current.columnWidths.length,
            ],
          }
        : {}),
    });
  };

  const addRow = () => {
    const current = tableDataRef.current;
    commitTableData({
      ...current,
      rows: [...current.rows, current.headers.map(() => emptyCellBlocks())],
    });
  };

  const deleteColumn = (colIndex) => {
    const current = tableDataRef.current;
    if (current.headers.length <= 1) return;
    commitTableData({
      ...current,
      headers: current.headers.filter((_, i) => i !== colIndex),
      rows: current.rows.map((row) => row.filter((_, i) => i !== colIndex)),
      ...(current.columnWidths
        ? { columnWidths: current.columnWidths.filter((_, i) => i !== colIndex) }
        : {}),
    });
  };

  const deleteRow = (rowIndex) => {
    const current = tableDataRef.current;
    if (current.rows.length <= 1) return;
    commitTableData({
      ...current,
      rows: current.rows.filter((_, i) => i !== rowIndex),
    });
  };

  const handleCellTab = (e, rowIndex, colIndex) => {
    if (e.key !== 'Tab') return;
    if (document.querySelector('.doc-slash-menu')) return;
    e.preventDefault();
    e.stopPropagation();
    const width = tableData.headers.length;
    if (e.shiftKey) {
      if (colIndex > 0) focusCell(rowIndex, colIndex - 1);
      else if (rowIndex === 0) focusCell(-1, width - 1);
      else if (rowIndex > 0) focusCell(rowIndex - 1, width - 1);
      else focusCell(-1, Math.max(0, colIndex - 1));
      return;
    }
    const nextCol = colIndex + 1;
    if (nextCol < width) {
      focusCell(rowIndex, nextCol);
    } else if (rowIndex === -1) {
      focusCell(0, 0);
    } else if (rowIndex < tableData.rows.length - 1) {
      focusCell(rowIndex + 1, 0);
    }
  };

  const renderCell = (cellBlocks, rowIndex, colIndex) => (
    <TableCellCanvas
      blocks={cellBlocks}
      onChange={(next) => handleCellBlocksChange(rowIndex, colIndex, next)}
      tableId={block.id}
      rowIndex={rowIndex}
      colIndex={colIndex}
      {...editorProps}
    />
  );

  const colWidths = tableData.columnWidths;
  const colSum = colWidths?.reduce((sum, w) => sum + w, 0) || 0;
  const lastDataCol = tableData.headers.length - 1;

  return (
    <>
      <div
        className={`table-block-wrapper${isResizing ? ' is-col-resizing' : ''}`}
        ref={wrapperRef}
      >
        <div
          ref={sentinelRef}
          className="table-sticky-sentinel"
          aria-hidden="true"
        />
        <div className="table-block">
          <table ref={tableElRef}>
            {colWidths && colSum > 0 ? (
              <colgroup>
                {colWidths.map((w, i) => (
                  <col key={i} style={{ width: `${(w / colSum) * 100}%` }} />
                ))}
                <col className="table-action-col" />
              </colgroup>
            ) : null}
            <thead ref={headerRef} className={stuck ? 'is-stuck' : undefined}>
              <tr>
                {tableData.headers.map((header, colIndex) => (
                  <th
                    key={colIndex}
                    style={
                      colWidths && colSum > 0
                        ? { width: `${(colWidths[colIndex] / colSum) * 100}%` }
                        : undefined
                    }
                    onKeyDownCapture={(e) => handleCellTab(e, -1, colIndex)}
                  >
                    {renderCell(header, -1, colIndex)}
                    {tableData.headers.length > 1 && (
                      <button
                        className="table-delete-col-btn"
                        onClick={() => deleteColumn(colIndex)}
                        title="Delete column"
                      >
                        ×
                      </button>
                    )}
                    {colIndex < lastDataCol ? (
                      <div
                        className="table-col-resize"
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize column"
                        onPointerDown={(e) => startResize(colIndex, e)}
                      />
                    ) : null}
                  </th>
                ))}
                <th className="table-add-col-cell">
                  <button className="table-add-btn" onClick={addColumn} title="Add column">
                    +
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {tableData.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, colIndex) => (
                    <td
                      key={colIndex}
                      onKeyDownCapture={(e) => handleCellTab(e, rowIndex, colIndex)}
                    >
                      {renderCell(cell, rowIndex, colIndex)}
                    </td>
                  ))}
                  <td className="table-row-actions">
                    {tableData.rows.length > 1 && (
                      <button
                        className="table-delete-row-btn"
                        onClick={() => deleteRow(rowIndex)}
                        title="Delete row"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-add-row-container">
            <button className="table-add-row-btn-bottom" onClick={addRow} title="Add row">
              + Add row
            </button>
          </div>
        </div>
      </div>
      {stickyHScrollBar}
    </>
  );
};
